/**
 * CRM > Pipeline > opportunity detail. Opportunity summary with inline edit,
 * a "close won/lost" action, an add-note box, and the full activity trail
 * (created / stage_change / field_change / note / won / lost).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { useSession } from '@/lib/useSession';
import { formatDateShort, formatDateTime } from '@/lib/dateUtils';
import {
  formatCrmMoney,
  formatProbabilityPct,
  hasCrmPipeline,
  oppStatusTone,
  useAddOppNote,
  useCloseOpportunity,
  useCrmOpportunity,
  useCrmReps,
  useUpdateOpportunity,
  type ActivityRow,
  type OpportunityRow,
  type UpdateOpportunityBody,
} from '@/lib/useCrm';

// Today as YYYY-MM-DD in the browser's local time (matches an <input type=date>).
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function CrmOpportunityDetail() {
  const { oppId } = useParams<{ oppId: string }>();
  const [searchParams] = useSearchParams();
  const backSearch = searchParams.toString();
  const { user } = useSession();
  // No more read/write levels: holding crm.pipeline grants both.
  const canRead = hasCrmPipeline(user?.permissions, user?.superAdmin);
  const canWrite = canRead;

  const detailQ = useCrmOpportunity(oppId);
  const backHref = { pathname: '/crm/opportunities', search: backSearch ? `?${backSearch}` : '' };

  if (user && !canRead) {
    return (
      <div className="flex h-full min-h-0 flex-col pb-4">
        <Link to={backHref} className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink">
          <ArrowLeft className="h-3 w-3" /> Back to pipeline
        </Link>
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">No access.</p>
      </div>
    );
  }

  const data = detailQ.data;
  const opp = data?.opportunity;

  return (
    <div className="flex h-full min-h-0 flex-col pb-4">
      <Link
        to={backHref}
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to pipeline
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">CRM / Pipeline</p>
          <h1 className="truncate text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">{opp?.title ?? 'Opportunity'}</h1>
          {opp && (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted font-mono">{opp.opp_no}</p>
          )}
        </div>
        {opp && (
          <Pill tone={oppStatusTone(opp.status)}>{opp.status === 'open' ? (opp.stage_name ?? 'Open') : opp.status}</Pill>
        )}
      </div>

      {detailQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : detailQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">Could not load: {detailQ.error.message}</p>
      ) : data && opp ? (
        <div className="min-h-0 flex-1 overflow-auto grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="space-y-5">
            <OpportunityFields opp={opp} canWrite={canWrite} />
            {canWrite && opp.status === 'open' && <ClosePanel oppId={opp.id} />}
          </div>
          <div className="space-y-5">
            {canWrite && <AddNotePanel oppId={opp.id} />}
            <ActivityPanel activity={data.activity} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function OpportunityFields({ opp, canWrite }: { opp: OpportunityRow; canWrite: boolean }) {
  const { reps } = useCrmReps();
  const update = useUpdateOpportunity();

  const seed = useMemo(
    () => ({
      title: opp.title,
      projected_amount: opp.projected_amount ?? '',
      probability: opp.probability != null ? String(Math.round(Number(opp.probability) * 100)) : '',
      expected_close_date: opp.expected_close_date ?? '',
      sales_rep_id: opp.sales_rep_id != null ? String(opp.sales_rep_id) : '',
      memo: opp.memo ?? '',
    }),
    [opp],
  );
  const [form, setForm] = useState(seed);
  useEffect(() => setForm(seed), [seed]);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    const body: UpdateOpportunityBody = {
      title: form.title.trim() || undefined,
      projected_amount: form.projected_amount ? Number(form.projected_amount) : undefined,
      probability: form.probability ? Math.min(1, Math.max(0, Number(form.probability) / 100)) : undefined,
      expected_close_date: form.expected_close_date || null,
      sales_rep_id: form.sales_rep_id ? Number(form.sales_rep_id) : null,
      memo: form.memo || null,
    };
    update.mutate({ id: opp.id, body });
  };

  const inputCls =
    'w-full border border-amp-line rounded px-2.5 py-1 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';
  const labelCls = 'block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-0.5';

  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-3">Opportunity</h2>
      <dl className="grid grid-cols-1 gap-y-1.5 text-sm sm:grid-cols-2 mb-4">
        <ReadRow label="Account">
          <Link to={`/crm/accounts/${opp.account_id}`} className="hover:underline">{opp.account_name ?? '--'}</Link>
        </ReadRow>
        <ReadRow label="Stage">{opp.stage_name ?? '--'}</ReadRow>
        <ReadRow label="Weighted">{formatCrmMoney(opp.weighted_amount)}</ReadRow>
        <ReadRow label="Probability">{formatProbabilityPct(opp.probability)}</ReadRow>
        {opp.status !== 'open' && <ReadRow label="Closed">{formatDateShort(opp.close_date)}</ReadRow>}
      </dl>

      {canWrite && opp.status === 'open' ? (
        <div className="space-y-2.5 border-t border-amp-line pt-3 dark:border-amp-dark-line">
          <div>
            <label className={labelCls}>Title</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Projected amount</label>
              <input type="number" step="0.01" value={form.projected_amount} onChange={(e) => set('projected_amount', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Probability (%)</label>
              <input type="number" min="0" max="100" value={form.probability} onChange={(e) => set('probability', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Expected close</label>
              <input type="date" value={form.expected_close_date} onChange={(e) => set('expected_close_date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Rep</label>
              <select value={form.sales_rep_id} onChange={(e) => set('sales_rep_id', e.target.value)} className={inputCls}>
                <option value="">Unassigned</option>
                {reps.map((r) => (
                  <option key={r.id} value={String(r.id)}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Memo</label>
            {/* Stays a plain box. Rendered as plain text on this page, and nothing
                sanitizes HTML on the CRM endpoints yet. Backend tracked in api#563,
                not approved. */}
            <textarea rows={2} value={form.memo} onChange={(e) => set('memo', e.target.value)} className={inputCls} />
          </div>
          {update.isError && <p className="text-xs text-red-700 dark:text-red-300">{update.error.message}</p>}
          {update.isSuccess && <p className="text-xs text-green-700 dark:text-green-300">Saved.</p>}
          <button
            type="button"
            onClick={submit}
            disabled={update.isPending}
            className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
          >
            {update.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
        </div>
      ) : (
        <dl className="grid grid-cols-1 gap-y-1.5 text-sm border-t border-amp-line pt-3 dark:border-amp-dark-line">
          <ReadRow label="Projected">{formatCrmMoney(opp.projected_amount)}</ReadRow>
          <ReadRow label="Expected close">{formatDateShort(opp.expected_close_date)}</ReadRow>
          <ReadRow label="Rep">{opp.sales_rep_name ?? '--'}</ReadRow>
          {opp.memo && <ReadRow label="Memo">{opp.memo}</ReadRow>}
        </dl>
      )}
    </div>
  );
}

function ClosePanel({ oppId }: { oppId: number }) {
  const close = useCloseOpportunity();
  const today = todayLocal();
  const [closeDate, setCloseDate] = useState(today);

  const inputCls =
    'border border-amp-line rounded px-2.5 py-1 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';

  return (
    <div className="border border-amp-line rounded-lg bg-amp-cream-warm p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
      <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-3">Close</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-0.5">Close date</label>
          <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} className={inputCls} />
        </div>
        <button
          type="button"
          onClick={() => close.mutate({ id: oppId, result: 'won', close_date: closeDate })}
          disabled={close.isPending}
          className="text-xs rounded px-3 py-1.5 border border-green-600 bg-green-600 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Mark won
        </button>
        <button
          type="button"
          onClick={() => close.mutate({ id: oppId, result: 'lost', close_date: closeDate })}
          disabled={close.isPending}
          className="text-xs rounded px-3 py-1.5 border border-red-600 bg-red-600 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Mark lost
        </button>
      </div>
      {close.isError && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{close.error.message}</p>}
    </div>
  );
}

function AddNotePanel({ oppId }: { oppId: number }) {
  const addNote = useAddOppNote();
  const [text, setText] = useState('');
  const submit = () => {
    const body = text.trim();
    if (!body) return;
    addNote.mutate({ id: oppId, body }, { onSuccess: () => setText('') });
  };
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-3">Add a note</h2>
      {/* Stays a plain box. Rendered as plain text on this page, and nothing
          sanitizes HTML on the CRM endpoints yet. Backend tracked in api#563,
          not approved. */}
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={5000}
        placeholder="Log a call, an email, or a next step..."
        className="w-full border border-amp-line rounded px-2.5 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
      />
      {addNote.isError && <p className="mt-1 text-xs text-red-700 dark:text-red-300">{addNote.error.message}</p>}
      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={submit}
          disabled={addNote.isPending || !text.trim()}
          className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
        >
          {addNote.isPending && <Loader2 className="h-3 w-3 animate-spin" />} Add note
        </button>
      </div>
    </div>
  );
}

function activityLabel(a: ActivityRow): string {
  switch (a.type) {
    case 'created': return 'Created';
    case 'note': return 'Note';
    case 'stage_change':
      return `Stage: ${a.from_stage_name ?? '--'} to ${a.to_stage_name ?? '--'}`;
    case 'field_change': return 'Edited';
    case 'won': return 'Closed won';
    case 'lost': return 'Closed lost';
    case 'reopened': return 'Reopened';
    default: return a.type;
  }
}

function ActivityPanel({ activity }: { activity: ActivityRow[] }) {
  return (
    <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <h2 className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-3">Activity</h2>
      {activity.length === 0 ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No activity yet.</p>
      ) : (
        <ul className="space-y-3">
          {activity.map((a) => (
            <li key={a.id} className="border-l-2 border-amp-line pl-3 dark:border-amp-dark-line">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">{activityLabel(a)}</span>
                <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateTime(a.created_at)}</span>
              </div>
              {a.body && <p className="text-sm text-amp-ink dark:text-amp-dark-ink break-words">{a.body}</p>}
              {a.actor && <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">{a.actor}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReadRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-28 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</dt>
      <dd className="flex-1 min-w-0 text-amp-ink dark:text-amp-dark-ink break-words">{children}</dd>
    </div>
  );
}
